import React, { useState, useEffect } from "react";
import styles from "./createNewLearningZoneModal.module.css";
import axios from "axios";

const CreateNewLearningZoneModal = ({ isOpen, onClose, onCreate }) => {
  /**
   * This component allows the user to create a new learning zone. The user should enter the name of the learning zone, and select the learned language. Upon successful creation, the user will be directed to the new learning zone.
   */

  const [learningZoneName, setLearningZoneName] = useState("");
  const [learnedLanguage, setlearnedLanguage] = useState("");
  const [languages, setLanguages] = useState([]);
  const [learningZoneNameError, setLearningZoneNameError] = useState("");
  const [learnedLanguageError, setlearnedLanguageError] = useState("");

  useEffect(() => {
    fetchLanguages();
  }, []);

  /**
   * Get the list of languages supported by the website, from the database.
   */
  const fetchLanguages = async () => {
    try {
      const response = await axios.get("/search/languages");
      setLanguages(response.data.languages);
    } catch (error) {
      console.error("Error fetching languages:", error);
    }
  };

  /* Validate the entered data, and if valid - create the new learning zone in the database. */
  const handleCreate = async () => {
    // Reset any previous error messages
    setLearningZoneNameError("");
    setlearnedLanguageError("");

    // Validation checks
    let isFormValid = true;

    if (!learningZoneName) {
      setLearningZoneNameError("Please enter a learning zone name.");
      isFormValid = false;
    }

    if (!learnedLanguage) {
      setlearnedLanguageError("Please select a language.");
      isFormValid = false;
    }

    if (!isFormValid) {
      return;
    }

    try {
      const formData = {
        learningZoneName,
        learnedLanguage,
      };

      const res = await axios.post("/learning_zones/create", formData);

      if (res.status === 200 && res.data.success) {
        onCreate(res.data.learning_zone_id); // Call the onCreate function with the new learning zone ID
      } else {
        console.error("Error - learning zone was not created.");
      }
    } catch (error) {
      console.error("Error - learning zone was not created.", error);
    }

    onClose();
  };

  /**
   * Handle change of the learning zone name. The name is limited to 50 characters.
   */
  const handleChangeLearningZoneName = (e) => {
    // Limit input length to 50 characters
    if (e.target.value.length <= 50) {
      setLearningZoneName(e.target.value);
      setLearningZoneNameError(""); // Clear error when user starts typing
    } else {
      setLearningZoneNameError(
        "Learning Zone Name must be up to 50 characters."
      );
    }
  };

  /**
   * Handle change of the selected language
   */
  const handleChangeLanguage = (e) => {
    setlearnedLanguage(e.target.value);
    setlearnedLanguageError(""); // Clear error when user selects a language
  };

  /**
   * Close the modal when clicking outside it.
   */
  const handleOverlayClick = (e) => {
    // Check if the click target is the modal overlay itself
    if (e.target.classList.contains(styles.modalOverlay)) {
      onClose();
    }
  };

  return (
    <div
      className={`${styles.modalOverlay} ${isOpen ? styles.modalOpen : ""}`}
      onClick={handleOverlayClick}
    >
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose}>
          &times;
        </button>
        <h2>Create a New Learning Zone</h2>
        <div className={styles.field}>
          <label>Learning Zone Name:</label>
          <input
            type="text"
            value={learningZoneName}
            onChange={handleChangeLearningZoneName}
            maxLength={50} // maxLength is set to 50 characters
          />
          {learningZoneNameError && (
            <small className={styles.error}>{learningZoneNameError}</small>
          )}
        </div>
        <div className={styles.field}>
          <label>Learned Language:</label>
          <select
            value={learnedLanguage}
            onChange={handleChangeLanguage}
            className={`${learnedLanguageError ? styles.errorInput : ""}`}
          >
            <option value="">Select a language</option>
            {languages.map((languageObj) => (
              <option key={languageObj.language} value={languageObj.language}>
                {languageObj.language}
              </option>
            ))}
            <option key="other" value="other">
              Other
            </option>
          </select>
          {learnedLanguageError && (
            <small className={styles.error}>{learnedLanguageError}</small>
          )}
        </div>
        <div className={styles.actions}>
          <button onClick={onClose} className={styles.cancelButton}>
            Cancel
          </button>
          <button onClick={handleCreate} className={styles.createButton}>
            Create
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateNewLearningZoneModal;
